import { memo, useCallback, useEffect, useRef, useState } from 'react';
import Konva from 'konva';
import type { KonvaEventObject } from 'konva/lib/Node';
import { Circle, Group, Image as KonvaImage, Layer, Line, Rect, Stage, Text, Transformer } from 'react-konva';
import type { PhotoElement, TextElement } from '../../shared/document';
import { useDocumentStore } from '../store/documentStore';
import { useUiStore } from '../store/uiStore';
import { binaryAssetRegistry } from '../store/binaryAssetRegistry';
import { fitCanvasToViewport, screenToDocument } from '../geometry/coordinates';
import { getBackgroundImageGeometry } from '../geometry/backgroundImage';
import { clampCropOffset, getPhotoDrawGeometry } from '../geometry/photoCrop';
import { BUILTIN_FONT_FAMILIES } from '../../shared/schemas';
import { systemFontFamily } from '../../shared/systemFonts';
import { CurvedTextArtwork } from './CurvedTextArtwork';
import { heightForCurveChange } from './textWarp';
import { getGrayscalePreview } from './grayscalePreview';

type Size = { width: number; height: number };

function PhotoArtwork({ element, cropEditing }: { element: PhotoElement; cropEditing: boolean }): React.JSX.Element | null {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [grayscaleImage, setGrayscaleImage] = useState<HTMLCanvasElement | null>(null);
  const imageNodeRef = useRef<Konva.Image>(null);

  useEffect(() => {
    setImage(null);
    if (!element.assetId) return;
    const blob = binaryAssetRegistry.get(element.assetId);
    if (!blob) return;
    const objectUrl = URL.createObjectURL(blob);
    const loadedImage = new window.Image();
    loadedImage.onload = () => setImage(loadedImage);
    loadedImage.onerror = () => URL.revokeObjectURL(objectUrl);
    loadedImage.src = objectUrl;
    return () => {
      loadedImage.onload = null;
      loadedImage.onerror = null;
      URL.revokeObjectURL(objectUrl);
    };
  }, [element.assetId]);

  useEffect(() => {
    setGrayscaleImage(null);
    if (!image || element.imageSaturation !== 0 || !element.assetId) return;
    setGrayscaleImage(getGrayscalePreview(image, element.assetId));
  }, [image, element.assetId, element.imageSaturation]);

  const hasEffects = element.imageBlur > 0 || element.imageBrightness !== 1 || (element.imageSaturation !== 1 && element.imageSaturation !== 0);
  const filters = [
    ...(element.imageBlur > 0 ? [Konva.Filters.Blur] : []),
    ...(element.imageBrightness !== 1 ? [Konva.Filters.Brighten] : []),
    ...(element.imageSaturation !== 1 && element.imageSaturation !== 0 ? [Konva.Filters.HSL] : []),
  ];
  useEffect(() => {
    const node = imageNodeRef.current;
    if (!node) return;
    node.clearCache();
    if (hasEffects) node.cache({ pixelRatio: 1 });
    node.getLayer()?.batchDraw();
  }, [image, grayscaleImage, hasEffects, element.imageBlur, element.imageBrightness, element.imageSaturation]);

  if (!image) return null;

  const { width, height } = element.transform;
  const geometry = getPhotoDrawGeometry(image.naturalWidth, image.naturalHeight, width, height, element);

  return (
    <Group
      x={element.transform.cx}
      y={element.transform.cy}
      offsetX={width / 2}
      offsetY={height / 2}
      rotation={element.transform.rotation}
      opacity={element.opacity}
      listening={cropEditing}
      clipFunc={(context) => {
        context.beginPath();
        context.roundRect(0, 0, width, height, Math.min(element.frame.radius, width / 2, height / 2));
      }}
    >
      <KonvaImage
        ref={imageNodeRef}
        x={geometry.x}
        y={geometry.y}
        width={geometry.width}
        height={geometry.height}
        offsetX={geometry.width / 2}
        offsetY={geometry.height / 2}
        rotation={geometry.rotation}
        scaleX={geometry.scaleX}
        scaleY={geometry.scaleY}
        image={element.imageSaturation === 0 && grayscaleImage ? grayscaleImage : image}
        opacity={element.imageOpacity}
        filters={filters}
        blurRadius={element.imageBlur}
        brightness={element.imageBrightness - 1}
        saturation={element.imageSaturation - 1}
        draggable={cropEditing}
        onClick={(event) => { if (cropEditing) event.cancelBubble = true; }}
        onTap={(event) => { if (cropEditing) event.cancelBubble = true; }}
        onDragStart={() => { if (cropEditing) useDocumentStore.getState().beginTransaction(); }}
        onDragMove={(event) => {
          if (!cropEditing) return;
          const offset = clampCropOffset(
            event.target.x() - width / 2,
            event.target.y() - height / 2,
            image.naturalWidth,
            image.naturalHeight,
            width,
            height,
            element,
          );
          useDocumentStore.getState().updateElement(element.id, {
            crop: { ...element.crop, ...offset },
          });
        }}
      />
    </Group>
  );
}

function CanvasBackgroundArtwork({
  assetId, width, height, opacity, blur, brightness, saturation, offsetX, offsetY,
}: { assetId: string | null; width: number; height: number; opacity: number; blur: number; brightness: number; saturation: number; offsetX: number; offsetY: number }): React.JSX.Element | null {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [grayscaleImage, setGrayscaleImage] = useState<HTMLCanvasElement | null>(null);
  const imageRef = useRef<Konva.Image>(null);
  useEffect(() => {
    setImage(null);
    if (!assetId) return;
    const blob = binaryAssetRegistry.get(assetId);
    if (!blob) return;
    const objectUrl = URL.createObjectURL(blob);
    const loadedImage = new window.Image();
    loadedImage.onload = () => setImage(loadedImage);
    loadedImage.onerror = () => URL.revokeObjectURL(objectUrl);
    loadedImage.src = objectUrl;
    return () => {
      loadedImage.onload = null;
      loadedImage.onerror = null;
      URL.revokeObjectURL(objectUrl);
    };
  }, [assetId]);
  useEffect(() => {
    setGrayscaleImage(null);
    if (!image || saturation !== 0 || !assetId) return;
    setGrayscaleImage(getGrayscalePreview(image, assetId));
  }, [image, assetId, saturation]);
  const hasEffects = blur > 0 || brightness !== 1 || (saturation !== 1 && saturation !== 0);
  const filters = [
    ...(blur > 0 ? [Konva.Filters.Blur] : []),
    ...(brightness !== 1 ? [Konva.Filters.Brighten] : []),
    ...(saturation !== 1 && saturation !== 0 ? [Konva.Filters.HSL] : []),
  ];
  // The cache bounds are derived from the fitted image geometry. When the
  // canvas changes from portrait to landscape (or vice versa), that geometry
  // changes even though the source image and its filters do not.
  const geometry = image
    ? getBackgroundImageGeometry(width, height, image.naturalWidth, image.naturalHeight, offsetX, offsetY)
    : null;
  // Background filters run synchronously in Konva. A full-size cache is
  // unnecessarily expensive in the editor preview, so cap its longest edge;
  // export renders separately at the document's full resolution.
  const cachePixelRatio = geometry
    ? Math.min(1, 1200 / Math.max(geometry.width, geometry.height))
    : 1;
  useEffect(() => {
    const node = imageRef.current;
    if (!node || !image) return;
    node.clearCache();
    if (hasEffects) node.cache({ pixelRatio: cachePixelRatio });
    node.getLayer()?.batchDraw();
  }, [image, grayscaleImage, blur, brightness, saturation, hasEffects, cachePixelRatio, width, height, offsetX, offsetY, geometry?.width, geometry?.height]);
  if (!image) return null;
  const fitted = geometry ?? getBackgroundImageGeometry(width, height, image.naturalWidth, image.naturalHeight, offsetX, offsetY);
  return (
    <Group clipFunc={(context) => { context.beginPath(); context.rect(0, 0, width, height); }} listening={false}>
      <KonvaImage
        ref={imageRef}
        image={saturation === 0 && grayscaleImage ? grayscaleImage : image}
        x={fitted.x}
        y={fitted.y}
        width={fitted.width}
        height={fitted.height}
        opacity={opacity}
        filters={filters}
        blurRadius={blur}
        brightness={brightness - 1}
        saturation={saturation - 1}
        listening={false}
      />
    </Group>
  );
}

function EditorStageView(): React.JSX.Element {
  const document = useDocumentStore((state) => state.document);
  const beginTransaction = useDocumentStore((state) => state.beginTransaction);
  const updateElement = useDocumentStore((state) => state.updateElement);
  const updateElements = useDocumentStore((state) => state.updateElements);
  const commitTransaction = useDocumentStore((state) => state.commitTransaction);
  const zoom = useUiStore((state) => state.zoom);
  const pan = useUiStore((state) => state.pan);
  const selectedElementId = useUiStore((state) => state.selectedElementId);
  const selectedElementIds = useUiStore((state) => state.selectedElementIds);
  const cropEditingId = useUiStore((state) => state.cropEditingId);
  const keepAspectRatio = useUiStore((state) => state.keepAspectRatio);
  const textWarpEditingId = useUiStore((state) => state.textWarpEditingId);
  const fitRequest = useUiStore((state) => state.fitRequest);
  const setSelectedElement = useUiStore((state) => state.setSelectedElement);
  const setSelectedElements = useUiStore((state) => state.setSelectedElements);
  const setCropEditing = useUiStore((state) => state.setCropEditing);
  const setZoom = useUiStore((state) => state.setZoom);
  const setPan = useUiStore((state) => state.setPan);
  const setTextWarpEditing = useUiStore((state) => state.setTextWarpEditing);
  const setTextEditing = useUiStore((state) => state.setTextEditing);
  const finishCropEdit = useCallback((commit: boolean) => {
    if (!cropEditingId) return;
    if (commit) useDocumentStore.getState().commitTransaction();
    else useDocumentStore.getState().cancelTransaction();
    setCropEditing(null);
  }, [cropEditingId, setCropEditing]);

  useEffect(() => {
    if (!cropEditingId) return;
    const handleKey = (event: KeyboardEvent): void => {
      if (event.key === 'Enter') { event.preventDefault(); finishCropEdit(true); }
      if (event.key === 'Escape') { event.preventDefault(); finishCropEdit(false); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [cropEditingId, finishCropEdit]);
  const viewportRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<Konva.Stage>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const elementNodes = useRef(new Map<string, Konva.Node>());
  const canvasDragStartPan = useRef<{ x: number; y: number } | null>(null);
  const middlePanStart = useRef<{ pointerX: number; pointerY: number; panX: number; panY: number } | null>(null);
  const groupDragStart = useRef<{ id: string; x: number; y: number; starts: Map<string, { x: number; y: number }> } | null>(null);
  const [isMiddlePanning, setIsMiddlePanning] = useState(false);
  const [rotationModeId, setRotationModeId] = useState<string | null>(null);
  const [textMoveTarget, setTextMoveTarget] = useState<{ id: string; x: number; y: number } | null>(null);
  const [size, setSize] = useState<Size>({ width: 0, height: 0 });
  const textTargetDragStart = useRef<{ id: string; targetX: number; targetY: number; textX: number; textY: number } | null>(null);

  useEffect(() => {
    const transformer = transformerRef.current;
    const nodes = selectedElementIds.flatMap((id) => {
      const element = document.elements.find((item) => item.id === id);
      const node = element && element.visible && !element.locked && !cropEditingId ? elementNodes.current.get(id) : undefined;
      return node ? [node] : [];
    });
    transformer?.nodes(nodes);
    transformer?.getLayer()?.batchDraw();
  }, [document.elements, selectedElementId, selectedElementIds, cropEditingId]);

  const selectFromPointer = (id: string, event: KonvaEventObject<MouseEvent>): void => {
    const current = useUiStore.getState().selectedElementIds;
    const command = event.evt.ctrlKey || event.evt.metaKey;
    if (command) setSelectedElements(current.includes(id) ? current.filter((item) => item !== id) : [...current, id], id);
    else if (event.evt.shiftKey) {
      const order = document.elements.map((item) => item.id);
      const from = order.indexOf(selectedElementId ?? id), to = order.indexOf(id);
      setSelectedElements(order.slice(Math.min(from, to), Math.max(from, to) + 1));
    } else setSelectedElement(id);
  };

  useEffect(() => {
    const handleMove = (event: MouseEvent): void => {
      const start = middlePanStart.current;
      if (!start) return;
      event.preventDefault();
      setPan(start.panX + event.clientX - start.pointerX, start.panY + event.clientY - start.pointerY);
    };
    const handleUp = (event: MouseEvent): void => {
      if (event.button !== 1 || !middlePanStart.current) return;
      event.preventDefault();
      middlePanStart.current = null;
      setIsMiddlePanning(false);
    };
    window.addEventListener('mousemove', handleMove, true);
    window.addEventListener('mouseup', handleUp, true);
    return () => {
      window.removeEventListener('mousemove', handleMove, true);
      window.removeEventListener('mouseup', handleUp, true);
    };
  }, [setPan]);

  const fitView = useCallback((width = size.width, height = size.height) => {
    if (width <= 0 || height <= 0) return;
    const fit = fitCanvasToViewport(width, height, document.canvas.width, document.canvas.height);
    setZoom(fit.zoom);
    setPan(fit.panX, fit.panY);
  }, [document.canvas.height, document.canvas.width, setPan, setZoom, size.height, size.width]);

  useEffect(() => {
    const container = viewportRef.current;
    if (!container) return;
    const observer = new ResizeObserver(([entry]) => {
      const width = entry?.contentRect.width ?? 0;
      const height = entry?.contentRect.height ?? 0;
      setSize({ width, height });
      fitView(width, height);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [fitView]);

  useEffect(() => {
    if (fitRequest > 0) fitView();
  }, [fitRequest, fitView]);

  const handleStageClick = (event: KonvaEventObject<MouseEvent>): void => {
    if (cropEditingId) finishCropEdit(true);
    if (event.target === event.target.getStage()) setSelectedElement(null);
  };

  const handleWheel = (event: KonvaEventObject<WheelEvent>): void => {
    event.evt.preventDefault();
    if (cropEditingId) {
      const photo = useDocumentStore.getState().document.elements.find((element) => element.id === cropEditingId);
      if (photo?.type === 'photo' && photo.fit === 'cover') {
        updateElement(photo.id, { crop: { ...photo.crop, zoom: Math.max(1, Math.min(10, photo.crop.zoom * (event.evt.deltaY > 0 ? 0.9 : 1.1))) } });
      }
      return;
    }
    const pointer = stageRef.current?.getPointerPosition();
    if (!pointer) return;
    const documentPoint = screenToDocument(pointer, { zoom, panX: pan.x, panY: pan.y });
    const nextZoom = Math.max(0.05, Math.min(8, zoom * (event.evt.deltaY > 0 ? 0.9 : 1.1)));
    setZoom(nextZoom);
    setPan(pointer.x - documentPoint.x * nextZoom, pointer.y - documentPoint.y * nextZoom);
  };

  const handleCanvasDragStart = (): void => {
    canvasDragStartPan.current = useUiStore.getState().pan;
  };

  const handleMiddleMouseDown = (event: KonvaEventObject<MouseEvent>): void => {
    if (event.evt.button !== 1) return;
    event.evt.preventDefault();
    event.cancelBubble = true;
    middlePanStart.current = {
      pointerX: event.evt.clientX,
      pointerY: event.evt.clientY,
      panX: useUiStore.getState().pan.x,
      panY: useUiStore.getState().pan.y,
    };
    setIsMiddlePanning(true);
  };

  const handleCanvasDragMove = (event: KonvaEventObject<DragEvent>): void => {
    const start = canvasDragStartPan.current;
    if (!start) return;
    setPan(start.x + event.target.x(), start.y + event.target.y());
  };

  const handleCanvasDragEnd = (event: KonvaEventObject<DragEvent>): void => {
    const start = canvasDragStartPan.current;
    if (start) setPan(start.x + event.target.x(), start.y + event.target.y());
    event.target.position({ x: 0, y: 0 });
    canvasDragStartPan.current = null;
  };

  return (
    <div className={`stage-viewport ${isMiddlePanning ? 'is-middle-panning' : ''}`} ref={viewportRef}>
      {size.width > 0 && size.height > 0 && (
        <Stage
          ref={stageRef}
          width={size.width}
          height={size.height}
          onClick={handleStageClick}
          onMouseDown={handleMiddleMouseDown}
          onTap={() => {
            if (stageRef.current?.getIntersection(stageRef.current.getPointerPosition() ?? { x: -1, y: -1 }) === null) {
              setSelectedElement(null);
            }
          }}
          onWheel={handleWheel}
        >
          <Layer>
            <Rect
              x={0}
              y={0}
              width={size.width}
              height={size.height}
              fill="rgba(0,0,0,0)"
              draggable={cropEditingId === null}
              onClick={() => setSelectedElement(null)}
              onTap={() => setSelectedElement(null)}
              onDragStart={handleCanvasDragStart}
              onDragMove={handleCanvasDragMove}
              onDragEnd={handleCanvasDragEnd}
            />
            <Group
              x={pan.x}
              y={pan.y}
              scaleX={zoom}
              scaleY={zoom}
            >
              <Rect
                x={0}
                y={0}
                width={document.canvas.width}
                height={document.canvas.height}
                fill={document.canvas.background ?? '#FFFFFF'}
                shadowColor="#000000"
                shadowBlur={24}
                shadowOpacity={0.22}
                listening={false}
              />
              <CanvasBackgroundArtwork
                assetId={document.canvas.backgroundAssetId}
                width={document.canvas.width}
                height={document.canvas.height}
                opacity={document.canvas.backgroundImageOpacity}
                blur={document.canvas.backgroundImageBlur}
                brightness={document.canvas.backgroundImageBrightness}
                saturation={document.canvas.backgroundImageSaturation}
                offsetX={document.canvas.backgroundImageOffsetX}
                offsetY={document.canvas.backgroundImageOffsetY}
              />
              {document.elements.map((element) => {
                const selected = selectedElementIds.includes(element.id);
                const editable = !element.locked && cropEditingId !== element.id;
                const commonProps = {
                  key: element.id,
                  x: element.transform.cx,
                  y: element.transform.cy,
                  width: element.transform.width,
                  height: element.transform.height,
                  offsetX: element.transform.width / 2,
                  offsetY: element.transform.height / 2,
                  rotation: element.transform.rotation,
                  draggable: editable,
                  onClick: (event: KonvaEventObject<MouseEvent>) => {
                    event.cancelBubble = true;
                    const alreadySelected = selectedElementIds.includes(element.id);
                    if (element.type === 'photo' && alreadySelected && !event.evt.ctrlKey && !event.evt.metaKey && !event.evt.shiftKey) {
                      setRotationModeId(element.id);
                      return;
                    }
                    setRotationModeId(null);
                    selectFromPointer(element.id, event);
                  },
                  onTap: () => setSelectedElement(element.id),
                  onDragStart: (event: KonvaEventObject<DragEvent>) => {
                    const dragSelection = selectedElementIds.includes(element.id) ? selectedElementIds : [element.id];
                    if (dragSelection.length === 1 && !selectedElementIds.includes(element.id)) setSelectedElement(element.id);
                    beginTransaction();
                    groupDragStart.current = { id: element.id, x: event.target.x(), y: event.target.y(), starts: new Map(dragSelection.filter((id) => id !== element.id).flatMap((id) => { const node = elementNodes.current.get(id); return node ? [[id, { x: node.x(), y: node.y() }] as const] : []; })) };
                  },
                  onDragMove: (event: KonvaEventObject<DragEvent>) => {
                    const start = groupDragStart.current;
                    if (!start || start.id !== element.id) return;
                    const dx = event.target.x() - start.x, dy = event.target.y() - start.y;
                    for (const [id, point] of start.starts) { const node = elementNodes.current.get(id); if (node) { node.x(point.x + dx); node.y(point.y + dy); } }
                  },
                  onDragEnd: () => {
                    const start = groupDragStart.current;
                    const ids = start?.id === element.id ? [element.id, ...start.starts.keys()] : [element.id];
                    updateElements(ids.flatMap((id) => { const item = document.elements.find((candidate) => candidate.id === id); const node = elementNodes.current.get(id); return item && node ? [{ id, patch: { transform: { ...item.transform, cx: node.x(), cy: node.y() } } }] : []; }));
                    groupDragStart.current = null;
                    commitTransaction();
                  },
                };

                if (element.type === 'photo') {
                  return (
                    <Group key={element.id} visible={element.visible}>
                      <Rect
                        {...commonProps}
                        ref={(node) => { if (node) elementNodes.current.set(element.id, node); else elementNodes.current.delete(element.id); }}
                        fill={element.frame.fill}
                        opacity={element.opacity * element.imageOpacity}
                        cornerRadius={element.frame.radius}
                        shadowEnabled={element.shadowBlur > 0 || element.shadowOffsetX !== 0 || element.shadowOffsetY !== 0}
                        shadowColor={element.shadowColor}
                        shadowBlur={element.shadowBlur}
                        shadowOffsetX={element.shadowOffsetX}
                        shadowOffsetY={element.shadowOffsetY}
                        visible={element.visible}
                        onClick={commonProps.onClick}
                        onTap={commonProps.onTap}
                        onDragStart={commonProps.onDragStart}
                        onDragMove={commonProps.onDragMove}
                        onDragEnd={commonProps.onDragEnd}
                      />
                      {element.assetId && <PhotoArtwork element={element} cropEditing={cropEditingId === element.id} />}
                      <Rect
                        x={element.transform.cx}
                        y={element.transform.cy}
                        width={element.transform.width}
                        height={element.transform.height}
                        offsetX={element.transform.width / 2}
                        offsetY={element.transform.height / 2}
                        rotation={element.transform.rotation}
                        cornerRadius={element.frame.radius}
                        fillEnabled={false}
                        stroke={selected ? '#4a56c6' : element.frame.borderColor}
                        strokeWidth={selected ? Math.max(2 / zoom, element.frame.borderWidth) : element.frame.borderWidth}
                        opacity={selected ? 1 : element.opacity * element.imageOpacity}
                        listening={false}
                      />
                      {!element.assetId && (
                        <Text
                          x={element.transform.cx}
                          y={element.transform.cy}
                          width={element.transform.width}
                          height={element.transform.height}
                          offsetX={element.transform.width / 2}
                          offsetY={element.transform.height / 2}
                          rotation={element.transform.rotation}
                          text="Prazen okvir"
                          align="center"
                          verticalAlign="middle"
                          fontSize={Math.max(16, 34 / zoom)}
                          fill="#767b87"
                          listening={false}
                        />
                      )}
                    </Group>
                  );
                }

                return (
                  <Group
                    key={element.id}
                    visible={element.visible}
                    onMouseEnter={() => {
                      const pointer = stageRef.current?.getPointerPosition();
                      if (!pointer) return;
                      const point = screenToDocument(pointer, { zoom, panX: pan.x, panY: pan.y });
                      setTextMoveTarget({ id: element.id, ...point });
                    }}
                    onMouseMove={() => {
                      if (textTargetDragStart.current) return;
                      const pointer = stageRef.current?.getPointerPosition();
                      if (!pointer) return;
                      const point = screenToDocument(pointer, { zoom, panX: pan.x, panY: pan.y });
                      setTextMoveTarget({ id: element.id, ...point });
                    }}
                    onMouseLeave={() => { if (!textTargetDragStart.current) setTextMoveTarget(null); }}
                  >
                    <CurvedTextArtwork
                      element={element}
                      family={document.fonts[element.fontId]?.family ?? BUILTIN_FONT_FAMILIES[element.fontId] ?? systemFontFamily(element.fontId) ?? 'sans-serif'}
                      draggable={editable}
                      onNode={(node) => { if (node) elementNodes.current.set(element.id, node); else elementNodes.current.delete(element.id); }}
                      onClick={(event) => {
                        event.cancelBubble = true;
                        // Konva's native dblclick callback can be missed when a
                        // draggable image is the hit target. The second click
                        // carries detail=2, so handle it here as well.
                        if (event.evt.detail >= 2) {
                          beginTransaction();
                          setTextEditing(element.id);
                          return;
                        }
                        selectFromPointer(element.id, event);
                        if (selectedElementId === element.id) setTextWarpEditing(textWarpEditingId === element.id ? null : element.id);
                        else setTextWarpEditing(null);
                      }}
                      onDoubleClick={(event) => {
                        event.cancelBubble = true;
                        if (useUiStore.getState().textEditingId !== element.id) beginTransaction();
                        setTextEditing(element.id);
                      }}
                      onDragStart={commonProps.onDragStart}
                      onDragMove={commonProps.onDragMove}
                      onDragEnd={commonProps.onDragEnd}
                    />
                    {textMoveTarget?.id === element.id && editable && (
                      <>
                        <Circle
                          x={textMoveTarget.x}
                          y={textMoveTarget.y}
                          radius={7 / zoom}
                          fill="#fff"
                          stroke="#17181c"
                          strokeWidth={2 / zoom}
                          draggable
                          onMouseDown={(event) => { event.cancelBubble = true; }}
                          onClick={(event) => {
                            event.cancelBubble = true;
                            if (event.evt.detail >= 2) {
                              if (useUiStore.getState().textEditingId !== element.id) beginTransaction();
                              setTextEditing(element.id);
                              return;
                            }
                            const alreadySelected = useUiStore.getState().selectedElementIds.includes(element.id);
                            selectFromPointer(element.id, event);
                            if (alreadySelected) setTextWarpEditing(textWarpEditingId === element.id ? null : element.id);
                            else setTextWarpEditing(null);
                          }}
                          onDblClick={(event) => {
                            event.cancelBubble = true;
                            if (useUiStore.getState().textEditingId !== element.id) beginTransaction();
                            setTextEditing(element.id);
                          }}
                          onTap={(event) => {
                            event.cancelBubble = true;
                            const alreadySelected = useUiStore.getState().selectedElementIds.includes(element.id);
                            selectFromPointer(element.id, event as KonvaEventObject<MouseEvent>);
                            if (alreadySelected) setTextWarpEditing(textWarpEditingId === element.id ? null : element.id);
                            else setTextWarpEditing(null);
                          }}
                          onDblTap={(event) => {
                            event.cancelBubble = true;
                            if (useUiStore.getState().textEditingId !== element.id) beginTransaction();
                            setTextEditing(element.id);
                          }}
                          onDragStart={(event) => {
                            event.cancelBubble = true;
                            const textNode = elementNodes.current.get(element.id);
                            if (!textNode) return;
                            setSelectedElement(element.id);
                            beginTransaction();
                            textTargetDragStart.current = {
                              id: element.id,
                              targetX: event.target.x(),
                              targetY: event.target.y(),
                              textX: textNode.x(),
                              textY: textNode.y(),
                            };
                          }}
                          onDragMove={(event) => {
                            event.cancelBubble = true;
                            const start = textTargetDragStart.current;
                            const textNode = elementNodes.current.get(element.id);
                            if (!start || start.id !== element.id || !textNode) return;
                            textNode.position({
                              x: start.textX + event.target.x() - start.targetX,
                              y: start.textY + event.target.y() - start.targetY,
                            });
                            textNode.getLayer()?.batchDraw();
                          }}
                          onDragEnd={(event) => {
                            event.cancelBubble = true;
                            const start = textTargetDragStart.current;
                            const textNode = elementNodes.current.get(element.id);
                            if (start?.id === element.id && textNode) {
                              updateElement(element.id, { transform: { ...element.transform, cx: textNode.x(), cy: textNode.y() } });
                              commitTransaction();
                            }
                            textTargetDragStart.current = null;
                            setTextMoveTarget(null);
                          }}
                        />
                        <Circle
                          x={textMoveTarget.x}
                          y={textMoveTarget.y}
                          radius={2 / zoom}
                          fill="#17181c"
                          stroke="#fff"
                          strokeWidth={1 / zoom}
                          listening={false}
                        />
                      </>
                    )}
                    {selected && (
                      <Rect
                        x={element.transform.cx}
                        y={element.transform.cy}
                        width={element.transform.width}
                        height={element.transform.height}
                        offsetX={element.transform.width / 2}
                        offsetY={element.transform.height / 2}
                        rotation={element.transform.rotation}
                        stroke="#4a56c6"
                        strokeWidth={2 / zoom}
                        dash={[6 / zoom, 4 / zoom]}
                        listening={false}
                      />
                    )}
                  </Group>
                );
              })}
              <Transformer
                ref={transformerRef}
                rotateEnabled={rotationModeId !== null && selectedElementIds.includes(rotationModeId)}
                flipEnabled={false}
                keepRatio={keepAspectRatio}
                rotationSnaps={[0, 90, 180, 270]}
                rotationSnapTolerance={4}
                anchorSize={9}
                borderStroke="#4a56c6"
                anchorStroke="#4a56c6"
                anchorFill="#ffffff"
                boundBoxFunc={(oldBox, newBox) => newBox.width < 12 || newBox.height < 12 ? oldBox : newBox}
                onTransformStart={beginTransaction}
                onTransformEnd={() => {
                  const current = useDocumentStore.getState().document;
                  const updates: Parameters<typeof updateElements>[0] = [];
                  for (const id of selectedElementIds) {
                    const node = elementNodes.current.get(id);
                    const element = current.elements.find((item) => item.id === id);
                    if (!element || !node) continue;
                    const scaleX = Math.abs(node.scaleX()), scaleY = Math.abs(node.scaleY());
                    node.scaleX(1); node.scaleY(1);
                    updates.push({ id: element.id, patch: {
                      transform: { cx: node.x(), cy: node.y(), width: Math.max(12, node.width() * scaleX), height: Math.max(12, node.height() * scaleY), rotation: node.rotation() },
                      ...(element.type === 'text' ? {
                        fontSize: Math.max(1, element.fontSize * scaleY),
                        // Curve is measured in canvas units. Scale it with the
                        // text box so resizing a curved label preserves its shape.
                        curve: element.curve * scaleY,
                      } : {}),
                    } });
                  }
                  updateElements(updates);
                  commitTransaction();
                }}
              />
              {(() => {
                const text = document.elements.find((element): element is TextElement => element.id === textWarpEditingId && element.type === 'text');
                if (!text) return null;
                const { width, rotation } = text.transform;
                const angle = rotation * Math.PI / 180;
                const point = (localX: number, localY: number): { x: number; y: number } => ({
                  x: text.transform.cx + localX * Math.cos(angle) - localY * Math.sin(angle),
                  y: text.transform.cy + localX * Math.sin(angle) + localY * Math.cos(angle),
                });
                const curvePoints = Array.from({ length: 25 }, (_, index) => {
                  const ratio = index / 24;
                  const localX = (ratio - 0.5) * width;
                  const localY = text.curve * Math.sin(Math.PI * ratio) + text.slant * localX;
                  const transformed = point(localX, localY);
                  return [transformed.x, transformed.y];
                }).flat();
                const toLocalY = (x: number, y: number): number => -(x - text.transform.cx) * Math.sin(angle) + (y - text.transform.cy) * Math.cos(angle);
                const updateWarp = (event: KonvaEventObject<DragEvent>, mode: 'curve' | 'left' | 'right'): void => {
                  const localY = toLocalY(event.target.x(), event.target.y());
                  if (mode === 'curve') {
                    const curve = Math.max(-4000, Math.min(4000, localY));
                    updateElement(text.id, { curve, transform: { ...text.transform, height: heightForCurveChange(text, curve) } });
                  }
                  else {
                    const side = mode === 'left' ? -1 : 1;
                    updateElement(text.id, { slant: Math.max(-1.5, Math.min(1.5, localY / (side * width / 2))) });
                  }
                };
                const handleProps = (mode: 'curve' | 'left' | 'right') => ({
                  onDragStart: beginTransaction,
                  onDragMove: (event: KonvaEventObject<DragEvent>) => updateWarp(event, mode),
                  onDragEnd: () => commitTransaction(),
                });
                const left = point(-width / 2, -text.slant * width / 2);
                const right = point(width / 2, text.slant * width / 2);
                const curve = point(0, text.curve);
                return <>
                  <Line points={curvePoints} stroke="#e17837" strokeWidth={2 / zoom} dash={[5 / zoom, 4 / zoom]} listening={false} />
                  <Circle x={curve.x} y={curve.y} radius={8 / zoom} fill="#e17837" stroke="#ffffff" strokeWidth={2 / zoom} draggable {...handleProps('curve')} />
                  <Circle x={left.x} y={left.y} radius={7 / zoom} fill="#3e8bcb" stroke="#ffffff" strokeWidth={2 / zoom} draggable {...handleProps('left')} />
                  <Circle x={right.x} y={right.y} radius={7 / zoom} fill="#3e8bcb" stroke="#ffffff" strokeWidth={2 / zoom} draggable {...handleProps('right')} />
                </>;
              })()}
            </Group>
          </Layer>
        </Stage>
      )}
    </div>
  );
}

export const EditorStage = memo(EditorStageView);
